import { sanitizeHtml } from './sanitizeHtml';

// Turns every URL in a plain-text value into a clickable link and returns HTML
// for `dangerouslySetInnerHTML`. Everything that isn't a URL is HTML-escaped,
// so user text can't inject markup.
//
// Matches http(s)://… and bare www.… URLs. Trailing punctuation that usually
// ends a sentence ("see https://a.com/x." / "(https://a.com)") stays outside
// the link. Done in one pass, so the same URL repeated in a field links every
// occurrence instead of re-wrapping the first one.

const URL_REGEX = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}]+$/;

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Keep a trailing ")" when the URL itself has a matching "(" (Wikipedia-style links).
const splitTrailing = (url: string): [string, string] => {
  const match = url.match(TRAILING_PUNCTUATION);
  if (!match) return [url, ''];
  let tail = match[0];
  let body = url.slice(0, url.length - tail.length);
  while (tail.startsWith(')') && (body.match(/\(/g) || []).length > (body.match(/\)/g) || []).length) {
    body += ')';
    tail = tail.slice(1);
  }
  return [body, tail];
};

export const linkify = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  let html = '';
  let last = 0;
  text.replace(URL_REGEX, (raw, offset: number) => {
    const [url, tail] = splitTrailing(raw);
    const href = /^www\./i.test(url) ? `https://${url}` : url;
    html += escapeHtml(text.slice(last, offset));
    html += `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(url)}</a>`;
    html += escapeHtml(tail);
    last = offset + raw.length;
    return raw;
  });
  html += escapeHtml(text.slice(last));
  return html;
};

// Rich-text (Quill) HTML: sanitize, then link URLs in text nodes only — tags,
// attributes and existing <a> elements are left as they are.
export const linkifyHtml = (html: unknown): string => {
  const clean = sanitizeHtml(html ? String(html) : '');
  if (!clean || typeof document === 'undefined') return clean;
  const holder = document.createElement('div');
  holder.innerHTML = clean;
  const walker = document.createTreeWalker(holder, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text);
  textNodes.forEach(node => {
    if (node.parentElement?.closest('a')) return;
    const text = node.nodeValue || '';
    URL_REGEX.lastIndex = 0;
    if (!URL_REGEX.test(text)) return;
    const span = document.createElement('span');
    span.innerHTML = linkify(text);
    node.replaceWith(...Array.from(span.childNodes));
  });
  holder.querySelectorAll('a[href]').forEach(anchor => {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });
  return holder.innerHTML;
};

export const containsUrl = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  URL_REGEX.lastIndex = 0;
  return URL_REGEX.test(value);
};

const looksLikeHtml = (value: string) => /<\/?[a-z][^>]*>/i.test(value);

// Plain text or rich-text HTML — whichever the field stores.
export const linkifyAny = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return looksLikeHtml(text) ? linkifyHtml(text) : linkify(text);
};

export default linkify;
